// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Form, InlineSpinner } from "@vector-im/compound-web";
import { useState } from "react";
import { defineMessage, FormattedMessage } from "react-intl";
import * as v from "valibot";

import { matrixPasswordLogin, wellKnownQuery } from "@/api/matrix";
import config from "@/config";
import { saveStaticCredential } from "@/stores/auth";
import { retryWhileTransient } from "@/utils/retry";

const LoginSearchParameters = v.object({
  redirect: v.optional(v.string()),
});

export const Route = createFileRoute("/_auth/login/")({
  validateSearch: LoginSearchParameters,
  staticData: {
    breadcrumb: {
      message: defineMessage({
        id: "pages.login.title",
        description: "Title for the login page",
        defaultMessage: "Login",
      }),
    },
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const serverName = config.serverName ?? "";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const {
    data: wellKnown,
    isFetching,
    error: discoveryError,
  } = useQuery({
    ...wellKnownQuery(serverName),
    ...retryWhileTransient,
    enabled: !!serverName,
    retry: false,
  });
  const synapseRoot = wellKnown?.["m.homeserver"].base_url;

  const login = useMutation({
    mutationFn: () => {
      if (!synapseRoot) throw new Error("Homeserver discovery is unavailable.");
      return matrixPasswordLogin(synapseRoot, username.trim(), password);
    },
    onSuccess: async ({ access_token }) => {
      await saveStaticCredential(serverName, access_token);
      await navigate({ to: redirect ?? "/", reloadDocument: true });
    },
    onError: (cause) => {
      setError(cause instanceof Error ? cause.message : "Sign in failed.");
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    login.mutate();
  };

  const isSecureContext = globalThis.isSecureContext;
  const submitDisabled =
    !synapseRoot ||
    !username.trim() ||
    !password ||
    login.isPending ||
    !isSecureContext;

  return (
    <Form.Root onSubmit={handleSubmit}>
      {!isSecureContext && (
        <p role="alert" className="text-text-critical-primary text-sm">
          <FormattedMessage
            id="pages.login.insecure_context"
            defaultMessage="Sign in requires a trusted HTTPS connection."
            description="Error shown when the login page is not served over HTTPS"
          />
        </p>
      )}
      {discoveryError && (
        <p role="alert" className="text-text-critical-primary text-sm">
          <FormattedMessage
            id="pages.login.discovery_error"
            defaultMessage="Could not discover the homeserver. Check the server name and its TLS certificate."
            description="Error shown when .well-known homeserver discovery fails"
          />
        </p>
      )}
      {error && (
        <p role="alert" className="text-text-critical-primary text-sm">
          {error}
        </p>
      )}

      <Form.Field name="username">
        <Form.Label>
          <FormattedMessage
            id="pages.login.username"
            defaultMessage="Username"
            description="Label for the username input on the login page"
          />
        </Form.Label>
        <Form.TextControl
          value={username}
          onInput={(event) => setUsername(event.currentTarget.value)}
          autoCapitalize="none"
          autoComplete="username"
          required
          size={1}
        />
      </Form.Field>

      <Form.Field name="password">
        <Form.Label>
          <FormattedMessage
            id="pages.login.password"
            defaultMessage="Password"
            description="Label for the password input on the login page"
          />
        </Form.Label>
        <Form.TextControl
          type="password"
          value={password}
          onInput={(event) => setPassword(event.currentTarget.value)}
          autoComplete="current-password"
          required
          size={1}
        />
      </Form.Field>

      <Form.Submit disabled={submitDisabled}>
        {login.isPending || isFetching ? <InlineSpinner /> : null}
        <FormattedMessage
          id="pages.login.sign_in"
          defaultMessage="Sign in"
          description="Button to sign in to the SDKmeet admin console"
        />
      </Form.Submit>
    </Form.Root>
  );
}
